/**
 * Punkt 134 — Aussehen je Widget (docs/PLAN-WIDGET-AUSSEHEN.md § 3–6).
 *
 * EIN Modul fuer API (Pruefung), Loader (Anwenden) und Editor (Vorschau, Kontrastwarnung).
 * Zod-frei, ohne DOM, ES2019 (Loader-Bundle unter 30 kB). Import: `@maestro/widgets/aussehen`.
 *
 * Schriften: nur System-Stacks, KEINE Webschrift wird nachgeladen (Google-Fonts-Nachladen waere
 * ein Datenschutz-Problem, LG Muenchen I 20.01.2022). „elegant“ sieht nur nach Playfair aus, wenn
 * die Website Playfair selbst laedt — sonst Georgia.
 */

/** Schluessel -> CSS-Stack. `website` = nichts setzen, das Widget erbt die Schrift der Seite. */
export const SCHRIFTEN: Readonly<Record<string, string>> = {
  website: "",
  serifenlos: 'system-ui,-apple-system,"Segoe UI",Roboto,sans-serif',
  serif: 'Georgia,"Times New Roman",serif',
  elegant: '"Playfair Display",Georgia,serif',
};

/** Ecken-Stufen in px: Eckig / Leicht / Rund / Sehr rund. */
export const ECKEN: readonly number[] = [0, 4, 8, 16];

export interface Aussehen {
  akzent?: string;
  knopfText?: string;
  schrift?: string;
  ecken?: number;
}

const HAT = Object.prototype.hasOwnProperty;
const HEX = /^#[0-9a-fA-F]{6}$/;

export const istHexFarbe = (v: unknown): v is string => typeof v === "string" && HEX.test(v);

/** `#rrggbb` oder `rgb()/rgba()` (wie getComputedStyle sie liefert) -> [r, g, b], sonst null. */
export function rgbVon(farbe: string): number[] | null {
  if (HEX.test(farbe)) return [1, 3, 5].map((i) => parseInt(farbe.slice(i, i + 2), 16));
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(farbe.trim());
  return m ? [+m[1]!, +m[2]!, +m[3]!] : null;
}

/** Relative Luminanz nach WCAG 2.1. */
function luminanz(rgb: number[]): number {
  const [r, g, b] = rgb.map((k) => {
    const c = k / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** Kontrastverhaeltnis (1 bis 21) zweier Farben, null bei unlesbarer Farbe. */
export function kontrast(a: string, b: string): number | null {
  const x = rgbVon(a);
  const y = rgbVon(b);
  if (!x || !y) return null;
  const l1 = luminanz(x);
  const l2 = luminanz(y);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** Schwarz oder Weiss — was auf diesem Hintergrund mehr Kontrast hat. */
export function lesbareTextfarbe(hintergrund: string): string | null {
  const rgb = rgbVon(hintergrund);
  if (!rgb) return null;
  const l = luminanz(rgb) + 0.05;
  return 1.05 / l >= l / 0.05 ? "#ffffff" : "#000000";
}

/** Server-Pruefung von `config.aussehen`: Fehlermeldung oder null. Unbekannte Schluessel = Fehler. */
export function pruefeAussehen(roh: unknown): string | null {
  if (roh === undefined) return null;
  if (typeof roh !== "object" || roh === null || Array.isArray(roh)) return "Die Angaben zum Aussehen sind ungültig.";
  for (const [k, v] of Object.entries(roh)) {
    if (k === "akzent" || k === "knopfText") {
      if (!istHexFarbe(v)) return "Bitte eine Farbe im Format #RRGGBB wählen.";
    } else if (k === "schrift") {
      if (typeof v !== "string" || !HAT.call(SCHRIFTEN, v)) return "Bitte eine Schrift aus der Liste wählen.";
    } else if (k === "ecken") {
      if (ECKEN.indexOf(v as number) < 0) return "Bitte eine Ecken-Stufe wählen (0, 4, 8 oder 16).";
    } else {
      return `Unbekannte Angabe beim Aussehen: ${k}`;
    }
  }
  return null;
}

/**
 * `config.aussehen` -> die `--maestro-*`-Variablen, die gesetzt werden sollen. Nur feste Werte
 * kommen durch (Hex-Farbe, Schrift aus der Tabelle, Ecken-Stufe) — kein CSS-Schmuggel.
 */
export function aussehenVariablen(roh: unknown): Record<string, string> {
  const a = (roh && typeof roh === "object" ? roh : {}) as Aussehen;
  const w: Record<string, string> = {};
  if (istHexFarbe(a.akzent)) w["--maestro-accent"] = a.akzent;
  if (istHexFarbe(a.knopfText)) w["--maestro-knopf-text"] = a.knopfText;
  const s = typeof a.schrift === "string" && HAT.call(SCHRIFTEN, a.schrift) ? SCHRIFTEN[a.schrift] : "";
  if (s) w["--maestro-font"] = s;
  if (typeof a.ecken === "number" && ECKEN.indexOf(a.ecken) >= 0) w["--maestro-radius"] = a.ecken + "px";
  return w;
}
